import { Address } from "@certn/cascadia";
import {
  Container,
  Layout,
  Loader,
  SectionMessage,
} from "@certn/cascadia-react";
import { JsonFormsCore, createAjv } from "@jsonforms/core";
import {
  ComponentType,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { I18nProvider } from "react-aria";
import { useNavigate } from "react-router-dom";

import { ApplicationContext } from "./ApplicationContext";
import { ApplicationFormContext } from "./ApplicationFormContext";
import { isApplicationSchema, SectionSchema } from "./types";
import {
  deepEqual,
  getAllSectionKeys,
  getDisabledSectionKeys,
  getNextSection,
  getPreviousSection,
} from "./utils";

interface Props extends PropsWithChildren {
  /**
   * Base path for all application routes (e.g. 'apply', 'quickscreen').
   * Defaults to 'apply'.
   */
  basePath?: string;
  id: string;
  schema: JsonFormsCore["schema"];
  uischema: JsonFormsCore["uischema"];
  data?: JsonFormsCore["data"];
  currentSection?: string;
  onRefresh: () => Promise<void> | void;
  onNavigateToSection: (
    section: string | SectionSchema,
    type?: "replace" | "navigate"
  ) => Promise<void> | void;
  onSave: (
    values: JsonFormsCore["data"],
    validate?: boolean
  ) => Promise<JsonFormsCore["data"]>;
  onSubmitApplication: () => Promise<void>;
  isSubmitting?: boolean;
  locale: string;
  customerName: string;
  clientGroupName: string;
  clientGroupAddress: Address;
  environment?: string;
  generatePresignedUrlEndpoint: string;
  downloadUrl?: string;
  sectionStatuses: Record<string, unknown>;
  branding:
    | {
        logo_url?: string | undefined;
        favicon_url?: string | undefined;
        org_logo_url_dark_mode?: string | undefined;
      }
    | {};
  /**
   * Custom component to render for the summary section.
   * If not provided, uses the default ApplicationSummary.
   */
  summaryComponent?: ComponentType;
  /**
   * Additional sections to render in ApplicationSectionList after the summary section.
   * Useful for app-specific sections like payment-details.
   */
  additionalSections?: ReactNode;
}

export function Application(props: Props) {
  const basePath = props.basePath ?? "apply";
  const navigate = useNavigate();
  const incompleteSectionSummaryRef = useRef<HTMLDivElement | null>(null);
  const {
    uischema,
    locale,
    currentSection,
    data,
    environment,
    id,
    generatePresignedUrlEndpoint,
    sectionStatuses,
    clientGroupName,
    clientGroupAddress,
  } = props;

  const formValues = useRef<object | undefined>(undefined);

  // We currently don't do anything with these 2 section statuses INCOMPLETE, SUBMITTED
  const invalidSections = Object.keys(sectionStatuses).filter(
    (sectionName) => sectionStatuses[sectionName] === "INVALID"
  );

  const completedSections = Object.keys(sectionStatuses).filter(
    (sectionName) => sectionStatuses[sectionName] === "COMPLETE"
  );

  const [submitting, setSubmitting] = useState(false);
  const saveDataPromise = useRef<Promise<void> | undefined>(undefined);

  useEffect(() => {
    if (isApplicationSchema(uischema)) {
      const disabledSectionKeys = getDisabledSectionKeys(
        uischema,
        data,
        createAjv()
      );

      const wrongSection =
        !currentSection ||
        !getAllSectionKeys(uischema).includes(currentSection) ||
        disabledSectionKeys.includes(currentSection);

      // payment-summary is a valid section used by PaymentSummaryPage but not in uischema
      if (
        wrongSection &&
        currentSection !== "summary" &&
        currentSection !== "payment-summary"
      ) {
        const firstAccessibleSection = uischema.elements.find(
          ({ name }) =>
            !completedSections?.includes(name) &&
            !disabledSectionKeys.includes(name)
        );

        navigate(
          `/${basePath}/${id}/${firstAccessibleSection?.name || "summary"}`,
          {
            replace: true,
          }
        );
      }
    }
  }, [
    basePath,
    completedSections,
    currentSection,
    data,
    id,
    navigate,
    uischema,
  ]);

  if (!props.currentSection) {
    return <Loader overlay />;
  }

  if (!isApplicationSchema(uischema)) {
    return (
      <Layout layout="narrow">
        <Container>
          <SectionMessage
            sectionTitle="Invalid schema"
            type="danger"
            icon="error"
          >
            Something went wrong reading the application schema.
          </SectionMessage>
        </Container>
      </Layout>
    );
  }

  const nextSection = getNextSection(currentSection, uischema, data);
  const prevSection = getPreviousSection(currentSection, uischema, data);

  const handleFormValuesChange = (values: object) => {
    formValues.current = values;
  };

  async function partiallySaveData() {
    if (saveDataPromise.current) {
      return await saveDataPromise.current;
    }

    if (deepEqual(data, formValues.current)) {
      return;
    }

    saveDataPromise.current = props.onSave(formValues.current, false);
    try {
      await saveDataPromise.current;
    } finally {
      saveDataPromise.current = undefined;
    }
  }

  return (
    <I18nProvider locale={locale}>
      <ApplicationContext.Provider
        value={{
          clientGroupName,
          clientGroupAddress,
          schema: props.schema,
          uischema,
          data: props.data || {},
          currentSection: props.currentSection,
          completedSections,
          invalidSections,
          nextSection: nextSection?.name,
          prevSection: prevSection?.name,
          customerName: props.customerName,
          refresh: props.onRefresh,
          navigateToSection: (section) => {
            if (section === "summary") {
              formValues.current = undefined;
            }
            props.onNavigateToSection(section);
          },
          submitApplication: async () => {
            try {
              setSubmitting(true);
              await props.onSubmitApplication();
            } finally {
              setSubmitting(false);
            }
          },
          isSubmitting: submitting,
          saveData: async (values, validate = true) => {
            if (saveDataPromise.current) {
              return await saveDataPromise.current;
            }
            saveDataPromise.current = props.onSave(values, validate);
            try {
              return await saveDataPromise.current;
            } finally {
              saveDataPromise.current = undefined;
            }
          },
          partiallySaveData,
          onChange: handleFormValuesChange,
          incompleteSectionSummaryRef:
            incompleteSectionSummaryRef as React.RefObject<HTMLDivElement>,
          appEnvironment: environment,
          locale,
          branding: props.branding,
          summaryComponent: props.summaryComponent,
          additionalSections: props.additionalSections,
          basePath,
        }}
      >
        <ApplicationFormContext.Provider
          value={{
            downloadUrl: props.downloadUrl,
            caseId: props.id,
            generatePresignedUrlEndpoint,
          }}
        >
          {props.children}
        </ApplicationFormContext.Provider>
      </ApplicationContext.Provider>
    </I18nProvider>
  );
}
